import {useEffect, useState} from 'react';
import {Accept, FileWithPath, useDropzone} from 'react-dropzone';
import {Controller, Path, useFormContext, useWatch} from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import {
  Box,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import {COLOR__PRIMARY} from '@/theme/colors';

interface Props<TFormValues extends object> {
  fieldName: Path<TFormValues>;
  acceptedFileTypes: Accept;
  label: string;
  existingFileUrl?: string;
  width: number | string;
  height: number | string;
  imageFit?: 'contain' | 'cover';
  emptyStateText?: string;
  alt?: string;
  // Called when the user clears an already-saved image (no newly selected
  // file). The parent is responsible for stopping passing `existingFileUrl`
  // and for sending the cleared value (e.g. null) on submit.
  onClearExisting?: () => void;
  // Draws a highlight frame around just the image box (not the label) —
  // e.g. to flag that this field differs from its originally loaded value.
  highlighted?: boolean;
}

export const ImageUploadField = <TFormValues extends object>({
  fieldName,
  acceptedFileTypes,
  label,
  existingFileUrl,
  width,
  height,
  imageFit = 'contain',
  emptyStateText = 'Select image',
  alt,
  onClearExisting,
  highlighted,
}: Props<TFormValues>) => {
  const {control, setValue, trigger} = useFormContext<TFormValues>();
  const currentFile = useWatch({
    control,
    name: fieldName,
  }) as FileWithPath | null;
  const [previewUrl, setPreviewUrl] = useState('');

  const {getInputProps, open: openFileSelectDialog} = useDropzone({
    accept: acceptedFileTypes,
    noClick: true,
    noKeyboard: true,
    onDrop: newlySelectedFiles => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setValue(fieldName, newlySelectedFiles[0] ?? null, {shouldDirty: true});
      void trigger(fieldName);
    },
  });

  useEffect(() => {
    if (!currentFile) {
      setPreviewUrl('');
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(currentFile);
    setPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [currentFile]);

  const displayedImageUrl = currentFile ? previewUrl : (existingFileUrl ?? '');

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({fieldState}) => (
        <Stack spacing={1.5}>
          <Typography>{label}</Typography>

          <Box
            onClick={currentFile ? undefined : openFileSelectDialog}
            position="relative"
            width={width}
            height={height}
            border="1px solid #E0E0E0"
            borderRadius={1}
            bgcolor="#fafafa"
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            sx={{
              cursor: currentFile ? 'default' : 'pointer',
              outline: highlighted ? `2px solid ${COLOR__PRIMARY}` : 'none',
              outlineOffset: 2,
            }}
          >
            <input {...getInputProps()} />

            {displayedImageUrl ? (
              <Box
                component="img"
                src={displayedImageUrl}
                alt={alt ?? label}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: imageFit,
                }}
              />
            ) : (
              <Stack spacing={1} alignItems="center" color="#B5B5B5">
                <ImageOutlinedIcon
                  sx={{
                    fontSize: 42,
                  }}
                />

                <Typography color="inherit">{emptyStateText}</Typography>
              </Stack>
            )}

            {displayedImageUrl && (currentFile || onClearExisting) ? (
              <IconButton
                onClick={event => {
                  event.stopPropagation();

                  if (currentFile) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    setValue(fieldName, null, {shouldDirty: true});
                    void trigger(fieldName);
                  } else {
                    onClearExisting?.();
                  }
                }}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  width: 28,
                  height: 28,
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,1)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Box>

          {fieldState.invalid ? (
            <FormHelperText error>{fieldState.error?.message}</FormHelperText>
          ) : null}
        </Stack>
      )}
    />
  );
};
